import React, { useEffect, useMemo, useState } from 'react';
import {
  Briefcase,
  Users as UsersIcon,
  Clock,
  CalendarPlus,
} from 'lucide-react';
import Panel from '../ui/Panel';
import ProgressBar from '../ui/ProgressBar';
import StatusPill from '../ui/StatusPill';
import EmptyState from '../ui/EmptyState';
import TablePagination from '../ui/TablePagination';
import {
  displayName,
  formatDateShort,
  groupByProject,
  lifecycleLabel,
  lifecyclePillClass,
} from './utilizationHelpers';

const DEFAULT_PAGE_SIZE = 10;

const BillableBadge = ({ billable }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
      billable
        ? 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-200'
        : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-300'
    }`}
  >
    {billable ? 'Billable' : 'Non-billable'}
  </span>
);

const ProjectCard = ({ group, onAddAllocation }) => {
  const activeMembers = group.members.filter(
    (m) => m.entry?.lifecycle !== 'ended',
  );
  const avgCapacity = activeMembers.length
    ? Math.round(group.planned_capacity / activeMembers.length)
    : 0;
  const billableHours = group.members
    .filter((m) => m.entry?.is_billable)
    .reduce((acc, m) => acc + Number(m.entry?.week_hours || 0), 0);
  const nonBillableHours = Math.max(
    0,
    Math.round((group.total_week_hours - billableHours) * 10) / 10,
  );

  return (
    <Panel
      data-testid={`project-card-${group.project_id}`}
      className="space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            <h3 className="text-sm font-semibold text-secondary-900 dark:text-white truncate">
              {group.project_name}
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-secondary-500 dark:text-secondary-400">
            {group.client && <span className="truncate">{group.client}</span>}
            <BillableBadge billable={group.is_billable} />
          </div>
        </div>
        <button
          type="button"
          onClick={() => onAddAllocation?.(group)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30"
          data-testid={`add-allocation-${group.project_id}`}
        >
          <CalendarPlus className="h-3.5 w-3.5" />
          Add allocation
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
            Members
          </div>
          <div className="text-base font-semibold text-secondary-900 dark:text-white inline-flex items-center gap-1">
            <UsersIcon className="h-3.5 w-3.5" />
            {activeMembers.length}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
            Planned capacity
          </div>
          <div className="text-base font-semibold text-secondary-900 dark:text-white">
            {group.planned_capacity}%
          </div>
        </div>
        <div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
            Tracked (week)
          </div>
          <div className="text-base font-semibold text-secondary-900 dark:text-white inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {group.total_week_hours.toFixed(1)}h
          </div>
        </div>
        <div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
            Billable mix
          </div>
          <div className="text-base font-semibold text-secondary-900 dark:text-white">
            {billableHours.toFixed(1)}h / {nonBillableHours.toFixed(1)}h
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
          Avg per-member capacity
        </div>
        <ProgressBar value={Math.min(100, avgCapacity)} />
      </div>

      <div className="divide-y divide-secondary-100 dark:divide-secondary-700/80 border-t border-secondary-100 dark:border-secondary-700/80">
        {group.members.map(({ user, entry }) => (
          <div
            key={`${group.project_id}-${user.id}`}
            className="py-2 flex items-center justify-between gap-2"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm text-secondary-900 dark:text-white truncate">
                {displayName(user)}
              </div>
              <div className="text-[11px] text-secondary-500 dark:text-secondary-400 truncate">
                {entry?.phase || 'No phase'} •{' '}
                {entry?.start_date
                  ? `${formatDateShort(entry.start_date)} → ${formatDateShort(
                      entry.end_date,
                    )}`
                  : 'No window'}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {entry?.allocation_percent != null && (
                <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
                  {entry.allocation_percent}%
                </span>
              )}
              <span className="text-xs text-secondary-600 dark:text-secondary-300">
                {Number(entry?.week_hours || 0).toFixed(1)}h
              </span>
              {entry?.lifecycle && (
                <StatusPill
                  status={lifecycleLabel(entry.lifecycle)}
                  colorClass={lifecyclePillClass(entry.lifecycle)}
                  showDot={false}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

const ProjectsView = ({
  users = [],
  projects = [],
  onAddAllocation,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
}) => {
  const groups = useMemo(() => groupByProject(users, projects), [users, projects]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset to page 1 whenever the underlying dataset changes (filters, range
  // switch, etc.) so the user never lands on a now-empty page.
  useEffect(() => {
    setPage(1);
  }, [groups.length]);

  const totalPages = Math.max(1, Math.ceil(groups.length / pageSize));
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return groups.slice(start, start + pageSize);
  }, [groups, page, pageSize]);

  if (!groups.length) {
    return (
      <Panel>
        <EmptyState
          icon={Briefcase}
          title="No project allocations yet"
          description="Create an allocation from any employee row, or add one here when projects have assigned members."
        />
      </Panel>
    );
  }

  return (
    <div className="space-y-4" data-testid="resource-projects-view">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {paged.map((group) => (
          <ProjectCard
            key={group.project_id}
            group={group}
            onAddAllocation={onAddAllocation}
          />
        ))}
      </div>
      {groups.length > pageSize && (
        <TablePagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={groups.length}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          itemLabel="projects"
        />
      )}
    </div>
  );
};

export default ProjectsView;
